import { foodPhotoDataUrlByteLength } from "./browser-photo";
import {
  FOOD_RECOGNITION_ENDPOINT,
  FOOD_RECOGNITION_SERVER_ERROR_CODES,
  MAX_FOOD_PHOTO_BYTES,
  FoodRecognitionError,
  type FoodRecognitionErrorCode,
  type FoodRecognitionResult,
  type FoodRecognitionServerErrorCode,
  type RecognizedFoodDraft,
} from "./types";

const DEFAULT_TIMEOUT_MS = 30_000;
const SUPPORTED_IMAGE_DATA_URL = /^data:image\/(?:jpeg|png|webp);base64,/i;

export interface FoodRecognitionClientOptions {
  fetch?: typeof fetch;
  endpoint?: string;
  requestTimeoutMs?: number;
}

export interface RecognizeFoodOptions {
  signal?: AbortSignal;
}

/** Narrow controller-facing port so recognition can be replaced in tests. */
export interface FoodRecognizer {
  recognize(image: string, options?: RecognizeFoodOptions): Promise<FoodRecognitionResult>;
}

export class FoodRecognitionClient implements FoodRecognizer {
  private readonly request: typeof fetch;
  private readonly endpoint: string;
  private readonly requestTimeoutMs: number;

  constructor(options: FoodRecognitionClientOptions = {}) {
    this.request = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.endpoint = options.endpoint ?? FOOD_RECOGNITION_ENDPOINT;
    this.requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_TIMEOUT_MS;
    if (!Number.isSafeInteger(this.requestTimeoutMs) || this.requestTimeoutMs <= 0) {
      throw new TypeError("Food recognition timeout must be a positive integer");
    }
  }

  async recognize(image: string, options: RecognizeFoodOptions = {}): Promise<FoodRecognitionResult> {
    if (options.signal?.aborted) throw options.signal.reason ?? new DOMException("Aborted", "AbortError");
    validateImageDataUrl(image);
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new FoodRecognitionError("Connect to the internet to identify food from a photo", "offline");
    }

    const timeout = new AbortController();
    const timeoutId = setTimeout(
      () => timeout.abort(new DOMException("Food recognition timed out", "TimeoutError")),
      this.requestTimeoutMs,
    );
    const signal = combineSignals(options.signal, timeout.signal);
    try {
      const response = await this.request(this.endpoint, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ image }),
        signal,
      });
      const body = await readJson(response);
      if (!response.ok) throw responseError(response, body);
      try {
        return parseRecognitionResult(body);
      } catch {
        throw new FoodRecognitionError("Food recognition returned an unexpected response", "invalid_response", response.status);
      }
    } catch (error) {
      if (error instanceof FoodRecognitionError) throw error;
      if (options.signal?.aborted) throw options.signal.reason ?? new DOMException("Aborted", "AbortError");
      if (timeout.signal.aborted) {
        throw new FoodRecognitionError("Food recognition took too long; try again", "timeout");
      }
      throw new FoodRecognitionError("Could not reach food recognition; check your connection", "network_error");
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

function validateImageDataUrl(image: string): void {
  if (typeof image !== "string" || !SUPPORTED_IMAGE_DATA_URL.test(image)) {
    throw new FoodRecognitionError("Use a JPEG, PNG, or WebP photo", "unsupported_image");
  }
  let bytes: number;
  try {
    bytes = foodPhotoDataUrlByteLength(image);
  } catch {
    throw new FoodRecognitionError("The photo data is invalid", "invalid_request");
  }
  if (bytes > MAX_FOOD_PHOTO_BYTES) {
    throw new FoodRecognitionError("The photo is too large to identify", "image_too_large");
  }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json() as unknown;
  } catch {
    if (response.ok) {
      throw new FoodRecognitionError("Food recognition returned an unexpected response", "invalid_response", response.status);
    }
    return undefined;
  }
}

function responseError(response: Response, body: unknown): FoodRecognitionError {
  const parsed = parseErrorBody(body);
  const code = parsed?.code ?? fallbackErrorCode(response.status);
  const message = parsed?.message ?? fallbackErrorMessage(code);
  return new FoodRecognitionError(
    message,
    code,
    response.status,
    code === "rate_limited" ? parseRetryAfterSeconds(response.headers.get("retry-after")) : undefined,
  );
}

function parseRetryAfterSeconds(value: string | null): number | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const retryAt = Date.parse(trimmed);
  return Number.isFinite(retryAt) ? Math.max(0, Math.ceil((retryAt - Date.now()) / 1_000)) : undefined;
}

function parseErrorBody(body: unknown): { code: FoodRecognitionServerErrorCode; message: string } | undefined {
  const root = record(body);
  const error = root && record(root.error);
  if (!error || !isServerErrorCode(error.code) || typeof error.message !== "string" || !error.message.trim()) return undefined;
  return { code: error.code, message: error.message.trim() };
}

function fallbackErrorCode(status: number): FoodRecognitionServerErrorCode {
  if (status === 413) return "image_too_large";
  if (status === 415) return "unsupported_image";
  if (status === 422) return "no_food";
  if (status === 429) return "rate_limited";
  if (status === 408 || status === 504) return "timeout";
  return status >= 500 ? "upstream_error" : "invalid_request";
}

function fallbackErrorMessage(code: FoodRecognitionErrorCode): string {
  switch (code) {
    case "image_too_large": return "The photo is too large to identify";
    case "unsupported_image": return "Use a JPEG, PNG, or WebP photo";
    case "no_food": return "No food could be identified in the photo";
    case "not_configured": return "Photo food recognition is not configured";
    case "rate_limited": return "Photo food recognition is busy; try again shortly";
    case "timeout": return "Food recognition took too long; try again";
    case "invalid_response": return "Food recognition returned an unexpected response";
    case "upstream_error": return "Food recognition is temporarily unavailable";
    case "offline": return "Connect to the internet to identify food from a photo";
    case "network_error": return "Could not reach food recognition; check your connection";
    default: return "The photo could not be processed";
  }
}

function parseRecognitionResult(input: unknown): FoodRecognitionResult {
  const root = requiredRecord(input);
  const draft = parseDraft(root.draft);
  const confidence = root.confidence;
  if (confidence !== "low" && confidence !== "medium" && confidence !== "high") throw new TypeError("Invalid confidence");
  if (!Array.isArray(root.assumptions) || root.assumptions.length > 8) throw new TypeError("Invalid assumptions");
  const assumptions = root.assumptions.map((assumption) => requiredString(assumption, 240));
  return {
    draft,
    confidence,
    description: requiredString(root.description, 500),
    assumptions,
  };
}

function parseDraft(input: unknown): RecognizedFoodDraft {
  const draft = requiredRecord(input);
  if (draft.basisAmount !== 1 || draft.basisUnit !== "serving") throw new TypeError("Invalid food basis");
  if (draft.sourceLabel !== "Photo estimate" || draft.requiresMacroReview !== true) throw new TypeError("Invalid estimate flags");
  if (!Array.isArray(draft.micronutrients) || draft.micronutrients.length !== 0) throw new TypeError("Invalid micronutrients");
  const brand = optionalString(draft.brand, 120);
  const servingName = optionalString(draft.servingName, 120);
  const proteinGrams = nonNegativeNumber(draft.proteinGrams, 2_000);
  const carbohydrateGrams = nonNegativeNumber(draft.carbohydrateGrams, 2_000);
  const fatGrams = nonNegativeNumber(draft.fatGrams, 1_000);
  const fibreGrams = nonNegativeNumber(draft.fibreGrams, 1_000);
  if (fibreGrams > carbohydrateGrams) throw new TypeError("Fibre cannot exceed carbohydrate");
  return {
    name: requiredString(draft.name, 120),
    ...(brand === undefined ? {} : { brand }),
    basisAmount: 1,
    basisUnit: "serving",
    ...(servingName === undefined ? {} : { servingName }),
    proteinGrams,
    carbohydrateGrams,
    fatGrams,
    fibreGrams,
    micronutrients: [],
    sourceLabel: "Photo estimate",
    requiresMacroReview: true,
  };
}

function requiredRecord(value: unknown): Record<string, unknown> {
  const parsed = record(value);
  if (!parsed) throw new TypeError("Expected an object");
  return parsed;
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function requiredString(value: unknown, maximumLength: number): string {
  if (typeof value !== "string" || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value)) {
    throw new TypeError("Expected text");
  }
  const text = value.normalize("NFKC").trim().replace(/\s+/gu, " ");
  if (!text || text.length > maximumLength) throw new TypeError("Expected text");
  return text;
}

function optionalString(value: unknown, maximumLength: number): string | undefined {
  if (value === undefined) return undefined;
  return requiredString(value, maximumLength);
}

function nonNegativeNumber(value: unknown, maximum: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > maximum) {
    throw new TypeError("Expected a bounded non-negative number");
  }
  return value;
}

function isServerErrorCode(value: unknown): value is FoodRecognitionServerErrorCode {
  return typeof value === "string" && (FOOD_RECOGNITION_SERVER_ERROR_CODES as readonly string[]).includes(value);
}

function combineSignals(...signals: Array<AbortSignal | undefined>): AbortSignal {
  const available = signals.filter((signal): signal is AbortSignal => Boolean(signal));
  if (available.length === 1) return available[0];
  if (typeof AbortSignal.any === "function") return AbortSignal.any(available);
  const controller = new AbortController();
  for (const signal of available) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}
